import { 
  BarChart3, Brain, Target, TrendingUp, Eye, Database, 
  Zap, Shield, Network, Users, FileCheck, AlertTriangle 
} from 'lucide-react';
import { ProductConfigs } from './types';

export const intelligenceSolutionsConfigs: ProductConfigs = {
  'business-intelligence-suite': {
    keyBenefits: [
      {
        icon: BarChart3,
        title: 'Real-Time Business Intelligence',
        description: 'Instant access to critical business metrics with live dashboards and automated alerts.',
        metrics: '80% faster decision-making',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Analytics Engine',
        description: 'Advanced forecasting models that predict business trends and opportunities.',
        metrics: '90% forecast accuracy',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Eye,
        title: 'Interactive Data Visualization',
        description: 'Powerful visualization tools that transform complex data into actionable insights.',
        metrics: '60% improvement in data comprehension',
        color: 'from-purple-500 to-pink-500'
      },
      {
        icon: Database,
        title: 'Unified Data Platform',
        description: 'Single source of truth connecting all your business data sources and systems.',
        metrics: '95% data integration coverage',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: FileCheck,
        title: 'Automated Reporting Suite',
        description: 'Intelligent reporting automation with customizable templates and scheduling.',
        metrics: '70% reduction in report generation time',
        color: 'from-cyan-500 to-blue-500'
      },
      {
        icon: Users,
        title: 'Collaborative Analytics',
        description: 'Team-based analytics with sharing, commenting, and collaborative insights.',
        metrics: '50% increase in data-driven collaboration',
        color: 'from-amber-500 to-orange-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Insights',
        description: 'Machine learning algorithms automatically discover hidden patterns and anomalies.',
        metrics: 'Identify 95% of business anomalies',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: Network,
        title: 'Advanced Data Modeling',
        description: 'Sophisticated data modeling capabilities with semantic layers and data relationships.',
        metrics: 'Support for 1000+ data dimensions',
        color: 'from-slate-500 to-zinc-500'
      },
      {
        icon: Zap,
        title: 'Real-Time Processing Engine',
        description: 'High-performance analytics engine processing millions of records in real-time.',
        metrics: 'Process 10M+ records per second',
        color: 'from-yellow-500 to-orange-500'
      },
      {
        icon: Target,
        title: 'Advanced Segmentation',
        description: 'Dynamic customer and business segmentation with AI-powered clustering.',
        metrics: '85% improvement in targeting accuracy',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: AlertTriangle,
        title: 'Intelligent Alerting System',
        description: 'Smart alerts that learn from user behavior and reduce noise while highlighting critical issues.',
        metrics: '90% reduction in false alerts',
        color: 'from-red-500 to-pink-500'
      },
      {
        icon: Database,
        title: 'Big Data Integration',
        description: 'Native support for big data platforms and cloud data warehouses.',
        metrics: 'Support for petabyte-scale data',
        color: 'from-blue-500 to-cyan-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Security Framework',
        description: 'Bank-grade security with end-to-end encryption and advanced access controls.',
        metrics: 'SOC 2 Type II + ISO 27001 certified',
        color: 'from-red-500 to-orange-500'
      },
      {
        icon: Database,
        title: 'High-Performance Architecture',
        description: 'Distributed cloud architecture optimized for large-scale analytics workloads.',
        metrics: '99.99% uptime SLA',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: FileCheck,
        title: 'Data Governance & Compliance',
        description: 'Comprehensive data governance with automated compliance reporting and audit trails.',
        metrics: '100% regulatory compliance coverage',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: Users,
        title: 'Advanced User Management',
        description: 'Sophisticated role-based access with row-level and column-level security.',
        metrics: 'Support for 100,000+ users',
        color: 'from-purple-500 to-pink-500'
      },
      {
        icon: AlertTriangle,
        title: '24/7 Monitoring & Support',
        description: 'Continuous system monitoring with proactive performance optimization.',
        metrics: '<30 second incident detection',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: TrendingUp,
        title: 'Auto-Scaling Performance',
        description: 'Intelligent auto-scaling that adapts to workload demands automatically.',
        metrics: 'Scale to 100x capacity instantly',
        color: 'from-cyan-500 to-blue-500'
      }
    ]
  },

  'predictive-analytics-engine': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'Advanced Machine Learning Models',
        description: 'State-of-the-art ML algorithms for accurate forecasting and trend prediction.',
        metrics: '95% prediction accuracy',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: TrendingUp,
        title: 'Business Forecasting Suite',
        description: 'Comprehensive forecasting tools for sales, demand, and financial planning.',
        metrics: '40% improvement in forecast precision',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: Target,
        title: 'Risk Assessment Engine',
        description: 'Advanced risk modeling and scenario analysis for strategic decision making.',
        metrics: '60% reduction in risk exposure',
        color: 'from-red-500 to-pink-500'
      },
      {
        icon: Eye,
        title: 'Pattern Recognition System',
        description: 'AI-powered pattern detection that identifies trends before they become obvious.',
        metrics: 'Detect trends 3-6 months early',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Database,
        title: 'Automated Insight Generation',
        description: 'Self-learning system that generates actionable insights automatically.',
        metrics: '80% reduction in analysis time',
        color: 'from-orange-500 to-amber-500'
      },
      {
        icon: BarChart3,
        title: 'Scenario Modeling Platform',
        description: 'Advanced what-if analysis with multiple scenario testing and optimization.',
        metrics: 'Test 1000+ scenarios simultaneously',
        color: 'from-indigo-500 to-purple-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'Real-Time Prediction Engine',
        description: 'Lightning-fast predictions with sub-second response times for real-time decisions.',
        metrics: '<100ms prediction response time',
        color: 'from-yellow-500 to-orange-500'
      },
      {
        icon: Network,
        title: 'Multi-Model Ensemble',
        description: 'Advanced ensemble methods combining multiple ML models for superior accuracy.',
        metrics: '99% model reliability score',
        color: 'from-slate-500 to-zinc-500'
      },
      {
        icon: FileCheck,
        title: 'Automated Model Training',
        description: 'Self-improving models that continuously learn and adapt to new data patterns.',
        metrics: 'Automatic model retraining',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: Brain,
        title: 'Deep Learning Integration',
        description: 'Advanced neural networks for complex pattern recognition and non-linear relationships.',
        metrics: 'Process 10M+ variables simultaneously',
        color: 'from-purple-500 to-pink-500'
      },
      {
        icon: AlertTriangle,
        title: 'Anomaly Detection Suite',
        description: 'Advanced outlier detection with intelligent filtering and contextual analysis.',
        metrics: '98% anomaly detection accuracy',
        color: 'from-red-500 to-orange-500'
      },
      {
        icon: Database,
        title: 'Feature Engineering Automation',
        description: 'Automated feature selection and engineering for optimal model performance.',
        metrics: '50% improvement in model accuracy',
        color: 'from-blue-500 to-cyan-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'AI Security Framework',
        description: 'Specialized security controls for AI/ML systems with model protection and audit trails.',
        metrics: 'NIST AI Risk Management compliant',
        color: 'from-red-500 to-orange-500'
      },
      {
        icon: Database,
        title: 'High-Performance Computing',
        description: 'GPU-accelerated computing infrastructure for complex ML workloads.',
        metrics: '1000x faster than traditional systems',
        color: 'from-green-500 to-teal-500'
      },
      {
        icon: FileCheck,
        title: 'Model Governance & Versioning',
        description: 'Complete model lifecycle management with versioning, rollback, and governance.',
        metrics: 'Track 100% of model changes',
        color: 'from-blue-500 to-indigo-500'
      },
      {
        icon: Users,
        title: 'Data Scientist Collaboration',
        description: 'Advanced collaboration tools for data science teams with experiment tracking.',
        metrics: 'Support for 1000+ concurrent experiments',
        color: 'from-purple-500 to-pink-500'
      },
      {
        icon: AlertTriangle,
        title: 'Model Monitoring & Drift Detection',
        description: 'Continuous monitoring for model performance degradation and data drift.',
        metrics: 'Real-time drift detection',
        color: 'from-orange-500 to-red-500'
      },
      {
        icon: TrendingUp,
        title: 'Elastic Scaling Architecture',
        description: 'Auto-scaling ML infrastructure that adapts to computational demands.',
        metrics: 'Scale from 1 to 10,000 models',
        color: 'from-cyan-500 to-blue-500'
      }
    ]
  }
};